import { cn } from "@/lib/cn";

export function Card({
  className,
  children,
  title,
  action,
  padded = true,
}: {
  className?: string;
  children: React.ReactNode;
  title?: React.ReactNode;
  action?: React.ReactNode;
  padded?: boolean;
}) {
  return (
    <section
      className={cn(
        "rounded-lg border border-line bg-surface shadow-[var(--shadow)]",
        className,
      )}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <h2 className="text-base font-semibold tracking-tight text-ink">{title}</h2>
          {action && <div className="text-xs text-ink-2">{action}</div>}
        </header>
      )}
      <div className={cn(padded && "p-4")}>{children}</div>
    </section>
  );
}

export type Tone = "neutral" | "info" | "ok" | "warn" | "bad" | "hold" | "orange";

const toneMap: Record<Tone, string> = {
  neutral: "bg-surface-2 text-ink-2 border-line",
  info: "bg-info-bg text-info border-transparent",
  ok: "bg-ok-bg text-ok border-transparent",
  warn: "bg-warn-bg text-warn border-transparent",
  bad: "bg-bad-bg text-bad border-transparent",
  hold: "bg-hold-bg text-hold border-transparent",
  orange: "bg-orange-soft text-orange border-transparent",
};

export function Badge({
  tone = "neutral",
  children,
  className,
  dot,
}: {
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium leading-4 whitespace-nowrap",
        toneMap[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Mono({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn("font-mono text-sm tnum", className)}>{children}</span>;
}

export const toneText: Record<Tone, string> = {
  neutral: "text-ink",
  info: "text-info",
  ok: "text-ok",
  warn: "text-warn",
  bad: "text-bad",
  hold: "text-hold",
  orange: "text-orange",
};

/** İkon kutuları için literal sınıf haritası (Tailwind dinamik sınıf üretmez). */
export const toneBox: Record<Tone, string> = {
  neutral: "bg-surface-2 text-ink-2",
  info: "bg-info-bg text-info",
  ok: "bg-ok-bg text-ok",
  warn: "bg-warn-bg text-warn",
  bad: "bg-bad-bg text-bad",
  hold: "bg-hold-bg text-hold",
  orange: "bg-orange-soft text-orange",
};

export const durumTone = {
  ok: "ok",
  islemde: "info",
  bloke: "bad",
  hold: "hold",
  na: "neutral",
} as const;
